import { useEffect, useState } from 'react'
import {
  Users,
  Mail,
  Trash2,
  UserPlus,
  Clock,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { usePartnerStore } from '@/store/partnerStore'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const paymentMethods = [
  { value: 'bank_transfer', label: 'Transferencia bancaria' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'wise', label: 'Wise (TransferWise)' },
]

function Field({ label, htmlFor, children, full = false, className = '' }) {
  return (
    <div className={`space-y-1.5 ${full ? 'sm:col-span-2' : ''} ${className}`}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

export default function Organization() {
  const { confirmar, dialogo } = useConfirmacion()
  const {
    profile,
    fetchProfile,
    updateProfile,
    team,
    fetchTeam,
    isLoading,
    addTeamMember,
    removeTeamMember,
  } = usePartnerStore()

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteData, setInviteData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'manager',
  })
  const [inviting, setInviting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    organization: { name: '', legalName: '', taxId: '', website: '', description: '' },
    contact: {
      phone: '',
      address: { street: '', city: '', state: '', country: '', zipCode: '' },
    },
    paymentInfo: {
      preferredMethod: 'bank_transfer',
      bankAccount: {
        bankName: '',
        accountNumber: '',
        routingNumber: '',
        accountHolder: '',
      },
      paypalEmail: '',
    },
  })

  useEffect(() => {
    loadProfile()
    fetchTeam()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadProfile = async () => {
    try {
      const data = await fetchProfile()
      if (data) {
        setFormData({
          organization: {
            name: data.organization?.name || '',
            legalName: data.organization?.legalName || '',
            taxId: data.organization?.taxId || '',
            website: data.organization?.website || '',
            description: data.organization?.description || '',
          },
          contact: {
            phone: data.contact?.phone || '',
            address: {
              street: data.contact?.address?.street || '',
              city: data.contact?.address?.city || '',
              state: data.contact?.address?.state || '',
              country: data.contact?.address?.country || '',
              zipCode: data.contact?.address?.zipCode || '',
            },
          },
          paymentInfo: {
            preferredMethod: data.paymentInfo?.preferredMethod || 'bank_transfer',
            bankAccount: {
              bankName: data.paymentInfo?.bankAccount?.bankName || '',
              accountNumber: data.paymentInfo?.bankAccount?.accountNumber || '',
              routingNumber: data.paymentInfo?.bankAccount?.routingNumber || '',
              accountHolder: data.paymentInfo?.bankAccount?.accountHolder || '',
            },
            paypalEmail: data.paymentInfo?.paypalEmail || '',
          },
        })
      }
    } catch {
      toast.error('Error al cargar perfil')
    }
  }

  const setSection = (section, field, value) =>
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }))

  const setNested = (section, parent, field, value) =>
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: { ...prev[section][parent], [field]: value },
      },
    }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await updateProfile(formData)
      toast.success('Perfil actualizado')
    } catch {
      toast.error('Error al actualizar perfil')
    } finally {
      setSaving(false)
    }
  }

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.firstName) {
      toast.error('Completa los campos obligatorios')
      return
    }
    setInviting(true)
    try {
      await addTeamMember({
        ...inviteData,
        _id: `mock-${Date.now()}`,
        lastLogin: null,
      })
      toast.success('Invitación enviada')
      setInviteOpen(false)
      setInviteData({ firstName: '', lastName: '', email: '', role: 'manager' })
    } catch {
      toast.error('Error al enviar invitación')
    } finally {
      setInviting(false)
    }
  }

  const handleRemoveMember = async (id) => {
    const ok = await confirmar({
      titulo: 'Quitar del equipo',
      descripcion: '¿Quitas a esta persona del equipo? Pierde el acceso al portal.',
      textoConfirmar: 'Quitar',
      variante: 'destructiva',
    })
    if (ok) {
      try {
        await removeTeamMember(id)
        toast.success('Miembro eliminado')
      } catch {
        toast.error('Error al eliminar miembro')
      }
    }
  }

  if (isLoading && !profile) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Organización"
        description="Información de tu organización y datos de pago"
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Información de la organización</CardTitle>
          </CardHeader>
          <CardContent>
            <Separator className="mb-4" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nombre de la organización *" htmlFor="org-name">
                <Input
                  id="org-name"
                  value={formData.organization.name}
                  onChange={(e) => setSection('organization', 'name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Razón social" htmlFor="org-legal">
                <Input
                  id="org-legal"
                  value={formData.organization.legalName}
                  onChange={(e) => setSection('organization', 'legalName', e.target.value)}
                />
              </Field>
              <Field label="NIT / ID Fiscal" htmlFor="org-tax">
                <Input
                  id="org-tax"
                  value={formData.organization.taxId}
                  onChange={(e) => setSection('organization', 'taxId', e.target.value)}
                />
              </Field>
              <Field label="Sitio web" htmlFor="org-web">
                <Input
                  id="org-web"
                  type="url"
                  value={formData.organization.website}
                  onChange={(e) => setSection('organization', 'website', e.target.value)}
                />
              </Field>
              <Field label="Descripción" htmlFor="org-desc" full>
                <Textarea
                  id="org-desc"
                  rows={3}
                  value={formData.organization.description}
                  onChange={(e) => setSection('organization', 'description', e.target.value)}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Información de contacto</CardTitle>
          </CardHeader>
          <CardContent>
            <Separator className="mb-4" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Teléfono" htmlFor="contact-phone">
                <Input
                  id="contact-phone"
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => setSection('contact', 'phone', e.target.value)}
                />
              </Field>
              <Field label="Dirección" htmlFor="contact-street">
                <Input
                  id="contact-street"
                  value={formData.contact.address.street}
                  onChange={(e) =>
                    setNested('contact', 'address', 'street', e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 mt-4">
              <Field label="Ciudad" htmlFor="contact-city">
                <Input
                  id="contact-city"
                  value={formData.contact.address.city}
                  onChange={(e) =>
                    setNested('contact', 'address', 'city', e.target.value)
                  }
                />
              </Field>
              <Field label="Estado / Provincia" htmlFor="contact-state">
                <Input
                  id="contact-state"
                  value={formData.contact.address.state}
                  onChange={(e) =>
                    setNested('contact', 'address', 'state', e.target.value)
                  }
                />
              </Field>
              <Field label="País" htmlFor="contact-country">
                <Input
                  id="contact-country"
                  value={formData.contact.address.country}
                  onChange={(e) =>
                    setNested('contact', 'address', 'country', e.target.value)
                  }
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Información de pago</CardTitle>
          </CardHeader>
          <CardContent>
            <Separator className="mb-4" />
            <Alert variant="info" className="mb-4">
              <AlertDescription>
                Esta información se utilizará para procesar tus comisiones. Asegúrate de
                que los datos sean correctos.
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Método preferido" htmlFor="pay-method">
                <Select
                  value={formData.paymentInfo.preferredMethod}
                  onValueChange={(v) => setSection('paymentInfo', 'preferredMethod', v)}
                >
                  <SelectTrigger id="pay-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {formData.paymentInfo.preferredMethod === 'bank_transfer' && (
              <div className="grid gap-4 sm:grid-cols-2 mt-4">
                <Field label="Nombre del banco" htmlFor="bank-name">
                  <Input
                    id="bank-name"
                    value={formData.paymentInfo.bankAccount.bankName}
                    onChange={(e) =>
                      setNested('paymentInfo', 'bankAccount', 'bankName', e.target.value)
                    }
                  />
                </Field>
                <Field label="Titular de la cuenta" htmlFor="bank-holder">
                  <Input
                    id="bank-holder"
                    value={formData.paymentInfo.bankAccount.accountHolder}
                    onChange={(e) =>
                      setNested(
                        'paymentInfo',
                        'bankAccount',
                        'accountHolder',
                        e.target.value
                      )
                    }
                  />
                </Field>
                <Field label="Número de cuenta" htmlFor="bank-acct">
                  <Input
                    id="bank-acct"
                    value={formData.paymentInfo.bankAccount.accountNumber}
                    onChange={(e) =>
                      setNested(
                        'paymentInfo',
                        'bankAccount',
                        'accountNumber',
                        e.target.value
                      )
                    }
                  />
                </Field>
                <Field label="Número de ruta (ABA / SWIFT)" htmlFor="bank-route">
                  <Input
                    id="bank-route"
                    value={formData.paymentInfo.bankAccount.routingNumber}
                    onChange={(e) =>
                      setNested(
                        'paymentInfo',
                        'bankAccount',
                        'routingNumber',
                        e.target.value
                      )
                    }
                  />
                </Field>
              </div>
            )}

            {formData.paymentInfo.preferredMethod === 'paypal' && (
              <div className="grid gap-4 sm:grid-cols-2 mt-4">
                <Field label="Email de PayPal" htmlFor="paypal-email">
                  <Input
                    id="paypal-email"
                    type="email"
                    value={formData.paymentInfo.paypalEmail}
                    onChange={(e) =>
                      setSection('paymentInfo', 'paypalEmail', e.target.value)
                    }
                  />
                </Field>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} size="lg">
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Guardando…
              </>
            ) : (
              'Guardar cambios'
            )}
          </Button>
        </div>
      </form>

      {/* Equipo */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users size={18} className="text-primary" />
            <CardTitle className="text-h3">Mi equipo</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          {Array.isArray(team) && team.length === 0 ? (
            <p className="text-body-sm text-subtle text-center py-4">
              No hay otros miembros en tu equipo.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {(team || [])
                .filter(Boolean)
                .map((member) => (
                  <div
                    key={member?._id || Math.random()}
                    className="rounded-lg border border-line bg-surface-muted p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-foreground">
                          {member?.firstName || ''} {member?.lastName || ''}
                        </p>
                        <p className="flex items-center gap-1 mt-1 text-caption text-subtle break-all">
                          <Mail size={12} className="shrink-0" /> {member?.email || 'N/A'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge
                          variant={member?.role === 'admin' ? 'default' : 'outline'}
                        >
                          {member?.role === 'admin' ? 'Admin' : 'Gestor'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleRemoveMember(member?._id)}
                          aria-label="Eliminar"
                          className="h-10 w-10 text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    <p className="flex items-center gap-1 mt-3 text-caption text-subtle">
                      <Clock size={12} /> Último acceso:{' '}
                      {member?.lastLogin
                        ? new Date(member.lastLogin).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                ))}
            </div>
          )}

          <div className="flex justify-center mt-4">
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus size={16} /> Invitar miembro
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invitar nuevo miembro</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="inv-fname">Nombre *</Label>
              <Input
                id="inv-fname"
                required
                value={inviteData.firstName}
                onChange={(e) =>
                  setInviteData({ ...inviteData, firstName: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-lname">Apellido</Label>
              <Input
                id="inv-lname"
                value={inviteData.lastName}
                onChange={(e) =>
                  setInviteData({ ...inviteData, lastName: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="inv-email">Email *</Label>
              <Input
                id="inv-email"
                type="email"
                required
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Rol</Label>
              <Select
                value={inviteData.role}
                onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                  <SelectItem value="manager">Gestor (lectura / operaciones)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviting}
            >
              Cancelar
            </Button>
            <Button onClick={handleInvite} disabled={inviting}>
              {inviting ? 'Enviando…' : 'Enviar invitación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dialogo}
    </PageContainer>
  )
}
